import { Component, ElementRef, Inject, Injectable, OnInit, ViewChild } from '@angular/core';
import { ColorType } from '../instant-analysis/models/color-type';
import { Analise, Projeto, TipoAnalise } from './models/analise';
import { TimestampTexts } from '../instant-analysis/models/timestamp-texts';
import { TimespanService } from '../instant-analysis/services/timespan/timespan.service';
import { DashboardToMapService } from './services/dashboard-to-map-service.service';
import { AppUserService } from './services/app-user.service';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { finalize, map } from "rxjs/operators";
import { MatDialog } from '@angular/material/dialog';
import { AddProjectDialogComponent } from './add-project-dialog/add-project-dialog.component';
import { routerToMainService } from '../instant-analysis/services/routerToMainService';
import { RemoveDialogComponent } from './remove-dialog/remove-dialog.component';
import { AuthService } from 'src/app/authentication/services/auth/auth.service';
import { MapConfigService } from '../shared/services/map-config/map-config.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { UploadJobsReaderService, UploadJob } from './services/upload-jobs-reader-service/upload-jobs-reader-service';

/**
 * Tela do Dashboard.
 *
 * Funcionalidades:
 * - (Para perfil admin) Upload de arquivo CSV de ocorrências.
 * - (Para perfil admin) Upload de arquivo CSV de Dados de Chuva.
 * - CRUD (Create, Read, Update, Delete) de projetos.
 * - Cada projeto possui uma lista de análises, que podem ser visualizadas ou
 *    excluídas.
 * - Uma análise é um registro dos valores dos campos de controle do mapa.
 *    Inclui coisas como quais camadas estão ativas, tipo de coloração
 *    (Contínua, discreta, constante) de cada camada, 
 *    percentis inferior e superior, horário de análise, regiões poligonais
 *    desenhadas no mapa etc.
 */
@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  providers: [
    {
      provide: 'rainUpload', 
      useFactory: (httpClient: HttpClient) => new UploadJobsReaderService(
        {
          intervalInMs: 60000,
          getUploadsRoute: 'chuva/upload',
          getStatusUpdateRoute: 'chuva/upload/status',
          startUploadRoute: 'chuva/upload',
          cancelUploadRoute: 'chuva/upload',
        }, 
        httpClient
      ),
      deps: [HttpClient],
    },
    {
      provide: 'eventUpload',
      useFactory: (httpClient: HttpClient) => new UploadJobsReaderService(
        {
          intervalInMs: 60000,
          getUploadsRoute: 'evento/upload',
          getStatusUpdateRoute: 'evento/upload/status',
          startUploadRoute: 'evento/upload',
          cancelUploadRoute: 'evento/upload',
        }, 
        httpClient
      ),
      deps: [HttpClient],
    },
  ]
})
export class DashboardComponent implements OnInit {

  /** ID do usuário conectado. */
  userID: number;

  /** ID do projeto a ser editado. */
  editableID: number;

  /** Lista de projetos do usuário conectado. */
  projects: Projeto[];

  /** Observable da lista dos projetos do usuário conectado. */
  projetos$:Observable<Projeto[]>;

  /** Observable da lista dos jobs de upload dos dados das chuvas. */
  rainUploadJobs$: Subject<any[]> = new Subject();

  /** Observable da lista dos jobs de upload dos dados das ocorrências. */
  eventUploadJobs$: Subject<any[]> = new Subject();

  /** Lista de jobs de upload dos dados de chuva. */
  rainUploadJobs: any[] = [];

  /** Lista de jobs de upload dos dados de ocorrências. */
  eventUploadJobs: any[] = [];

  /** Referência ao projeto selecionado pelo usuário. */
  selectedProject: Projeto;

  /** Controla o que está sendo exibido na área central da tela do dashboard. */
  isPanelActive: {[key: string]: boolean} = {
    'project': true,
    'missingRainData': false,
    'rain-event': false
  }

  /** id de intervalo da função setInterval para monitorar o progresso do upload das chuvas. */
  rainUploadIntervalId: NodeJS.Timeout;

  /** id do setInterval para monitorar o progresso do upload das ocorrências. */
  eventUploadIntervalId: NodeJS.Timeout;

  //uploadType define qual tipo de upload deve ser disposto na tela
  uploadType = 'none';

  /**
   * Construtor.
   * 
   * @param router router para navegar para a tela de análise instantânea / histórica.
   * @param appUserService serviço de dados do usuário conectado.
   * @param dialog componente de dialog do angular material.
   * @param routerMessager serviço de comunicação com o componente main menu.
   * @param authService serviço de autenticação.
   */
  constructor(
    private snackBar: MatSnackBar,
    private appUserService: AppUserService,
    public dialog: MatDialog,
    private authService: AuthService, 
    private mapConfigService: MapConfigService,
    private timespanService: TimespanService,
    @Inject('rainUpload') private rainUploadService: UploadJobsReaderService,
    @Inject('eventUpload') private eventUploadService: UploadJobsReaderService,) { }

  /**
   * Ao iniciar o componente, busca o ID do usuário e a lista de projetos 
   * associados a ele.
   */
  ngOnInit(): void {
    this.userID = JSON.parse(localStorage.getItem('userInfo')).id
    this.getUserInformation()

    this.rainUploadService.subject.subscribe(jobs => {
      this.rainUploadJobs = jobs;
    });

    this.eventUploadService.subject.subscribe(jobs => {
      this.eventUploadJobs = jobs;
    })

    this.rainUploadService.constantFetch();
    this.eventUploadService.constantFetch();
  }

  /**
   * Solicita ao serviço de autenticação a verificação da role do usuário conectado.
   * @returns true se o role do usuário for 'admin', false caso contrário. 
   */
  isAdmin() {
    return this.authService.isAdmin() == true;
  }

  /**
   * Controla o conteúdo a ser exibido na região central da tela.
   * @param name nome da região a ser exibida. Usado como chave de isPanelActive.
   */
  setPanelActive(name: string) {
    Object.keys(this.isPanelActive).forEach(key => {
      this.isPanelActive[key] = false;
    });
    this.isPanelActive[name] = true;
    this.selectedProject = undefined;
    this.uploadType = 'none';
  }

  /**
   setRainEvent define qual painel sera mostrado de acordo com o clique nos botoes 
   */

  setRainEvent(event: string) {
    this.uploadType = event;
  }
  /**
   * Obtém uma string para o tipo da análise.
   * 
   * @param analise pode ser uma análise instantânea ou histórica, dependendo
   *    do valor no campo tipo.
   * @returns string "Análise Instantânea" ou "Análise Histórica", dependendo
   *    do valor do campo tipo do objeto analise.
   */
  getAnalisisType(analise: Analise){
    if (analise.tipo == TipoAnalise.INSTANTANEA) return "Análise Instantânea"
    else return "Análise Intervalar"
  }

  /**
   * Formata um objeto Date como string
   * 
   * @param date data a ser formatada
   * @returns string no formato "dd/mm/YYYY HH:MM"
   */
  formatDate(date:Date){
   return this.timespanService.formatDatetime(date);
  }

  /**
   * Trata a navegação para a tela do mapa, para visualizar uma análise.
   * Passa uma string numérica para o routerMessager. O componente MainMenu usa
   * esse valor.
   * Passa o objeto da análise para o router. As telas de análise instantânea e
   * histórica usam esse objeto para restaurar o estado do mapa.
   * 
   * @param analise análise cujo botão de entrar foi clicado.
   */
  confirmAnalise(analise:Analise){
    // if (this.getAnalisisType(analise) == "Análise Instantânea") this.routerMessager.enforceRouterIdentity('1')
    // else this.routerMessager.enforceRouterIdentity('2')
    // this.router.setRouterInformation(analise);
    this.mapConfigService.setMapConfigFromAnalise(analise);
  }

  /**
   * Exclui a análise selecionada.
   * @param analise análise a ser excluída.
   */
  deleteAnalise(analise:Analise){
    const dialogRef = this.dialog.open(RemoveDialogComponent,{
      width: '250px',
      data:analise
    })

    dialogRef.afterClosed().subscribe(result=>{
      if (result)
        this.appUserService.removeAnalysisFromProject(analise.id).subscribe(val=>{
          this.projects.forEach(val=>{
            if (val.id == analise.id_projeto){
              val.analises = val.analises.filter(val=>val.id!=analise.id)
            }
          })
        })
    })
  }

  /**
   * Gera o link para o componente da análise instantânea ou histórica,
   * dependendo do tipo da análise.
   * 
   * @param item tipo da análise.
   * @returns "/map" se a análise for instantânea, "/history" se for histórica.
   */
  getRouterLink(item: TipoAnalise){
    if(item == TipoAnalise.INSTANTANEA) return "/sistema/map"
    else return "/sistema/history"
  }


  /**
   * Exclui o projeto selecionado, junto com as análises subjacentes.
   * @param projeto projeto a ser excluído.
   */
  deleteProject(projeto:Projeto){
    const dialogRef = this.dialog.open(RemoveDialogComponent,{
      width: '250px',
      data:projeto
    })

    dialogRef.afterClosed().subscribe(result=>{
      if (result){
        this.selectedProject = undefined;
        this.appUserService.removeProject(projeto.id).subscribe();
        this.projects = this.projects.filter(val=>{return val.id != projeto.id})
      }
    }) 
  }

  /**
   * Adiciona um projeto vinculado ao usuário conectado.
   */
  addProject(){
    const dialogRef = this.dialog.open(AddProjectDialogComponent, {
      width: '250px',
      data:{id:null,id_app_user:this.userID,nome:""}
    });

    dialogRef.afterClosed().subscribe(result => {
      this.appUserService.addProject(result).subscribe(
        val => {
          this.getUserInformation()
        }
      );
    });
  }

  /**
   * Habilita o campo de edição do nome do projeto.
   * @param projeto projeto cujo nome será editado.
   */
  enableEdit(projeto:Projeto){
    let span = document.getElementById(projeto.id.toString());
    span.focus()
    this.editableID = projeto.id
  }

  /**
   * Salva estado do projeto editado.
   * @param projeto projeto editado.
   */
  editProject(projeto:Projeto){
    if (this.editableID == projeto.id){
      this.appUserService.editProject(projeto).subscribe() 
      this.editableID = -1
    }
  }


  /**
   * Busca os dados dos projetos e análises do usuário conectado.
   */
  getUserInformation(){
    this.projetos$ = this.appUserService.getAllProjectsFromUser(this.userID);
    this.projetos$.subscribe(val=>{
      this.projects = val;
    });
  }

  /**
   * Marca um projeto como selecionado, para exibir suas análises na área
   * central da tela.
   * @param projeto projeto selecionado para exibição.
   */
  openProject(projeto:Projeto){

    if (this.selectedProject == projeto) {
      this.selectedProject = undefined;
    }
    else {
      this.selectedProject = projeto;
    }


  }

  /**
   * Trata o evento do upload dos arquivos de chuvas, solicitando a atualização da tabela dos jobs de upload de chuvas.
   */
  async onRainUploadStarted() {
    this.rainUploadService.constantFetch();
  }

  /**
   * Dispara a busca por jobs de upload de ocorrências e armazena o id do setInterval usado para disparar consultas subsequentes que visam monitorar o progresso do job.
   */
  async onEventUploadStarted() {
    this.eventUploadService.constantFetch();
  }

  /**
   * Trata o evento de cancelamento do upload das planilhas de chuva.
   * @param job 
   */
  cancelRainUploadJob(job: UploadJob) {

    this.rainUploadService.cancel(job).subscribe(r => {
      this.rainUploadService.constantFetch();
      this.snackBar.open(r.message, '', { duration: 6000 });
    })
  }

  /**
   * Trata o evento de cancelamento do upload dos arquivos de ocorrências.
   * @param job 
   */
  cancelEventUploadJob(job: UploadJob) {

    this.eventUploadService.cancel(job).subscribe(r => {
      this.eventUploadService.constantFetch();
      this.snackBar.open(r.message, '', { duration: 6000 });
    });
  }
}
